import { PATH_SEGMENTS } from '../config/paths';
import { api, fetchData, postData } from './api';

const la = (...parts: (string | number)[]) => api(PATH_SEGMENTS.LIVE_ASSIST, ...parts);

export interface CreateLiveAssistSessionBody {
  mode: string;
  input_source_type: string;
  interaction_id?: number | null;
  department?: string | null;
  viewer_user_dpt_ids?: number[];
}

export interface LiveAssistSessionPayload {
  session_id: string;
  tenant_id?: number;
  owner_user_dpt_id?: number;
  mode: string;
  input_source_type: string;
  status: string;
  turn_count: number;
  interaction_id?: number | null;
  viewer_user_dpt_ids?: number[];
  audio_provider_session_id?: string | null;
  voice_bot_audio_enabled?: boolean;
  audio_ingress_mode?: string;
  runtime_state?: Record<string, unknown>;
  [key: string]: unknown;
}

type Envelope<T> = { message: string; data: T };

async function unwrapPost<R>(promise: Promise<Envelope<R>>): Promise<R> {
  const res = await promise;
  return res.data;
}

export async function liveAssistCreateSession(
  body: CreateLiveAssistSessionBody
): Promise<{
  session: LiveAssistSessionPayload;
  turn?: number;
  result?: Record<string, unknown>;
}> {
  return unwrapPost(
    postData<
      Envelope<{
        session: LiveAssistSessionPayload;
        turn?: number;
        result?: Record<string, unknown>;
      }>
    >(la('sessions'), body)
  );
}

export async function liveAssistGetSession(sessionId: string): Promise<LiveAssistSessionPayload> {
  const res = await fetchData<Envelope<{ session: LiveAssistSessionPayload }>>(la('sessions', sessionId));
  return res.data.session;
}

export async function liveAssistCloseSession(sessionId: string): Promise<{ session: LiveAssistSessionPayload }> {
  return unwrapPost(
    postData<Envelope<{ session: LiveAssistSessionPayload }>>(la('sessions', sessionId, 'close'), {})
  );
}

/** Upload original mic capture to server (call before close while session is active). */
export async function liveAssistUploadRecording(
  sessionId: string,
  blob: Blob,
  filename: string
): Promise<{ interaction_id: number; filename: string }> {
  const fd = new FormData();
  fd.append('file', blob, filename);
  return unwrapPost(
    postData<Envelope<{ interaction_id: number; filename: string }>>(la('sessions', sessionId, 'recording'), fd)
  );
}

export async function liveAssistProcessTurn(
  sessionId: string,
  body: { speaker: string; utterance: string; start_ms?: number; end_ms?: number }
): Promise<{ session: LiveAssistSessionPayload; turn: number; result: Record<string, unknown>; opening_turn?: { turn?: number; result?: Record<string, unknown> } }> {
  return unwrapPost(
    postData<Envelope<{ session: LiveAssistSessionPayload; turn: number; result: Record<string, unknown>; opening_turn?: { turn?: number; result?: Record<string, unknown> } }>>(
      la('sessions', sessionId, 'turns'),
      body
    )
  );
}

export async function liveAssistStartAudio(sessionId: string): Promise<{
  session_id?: string;
  audio_provider_session_id?: string;
  mode?: string;
  media_ws_path?: string;
  turn?: number;
  result?: Record<string, unknown>;
  session?: LiveAssistSessionPayload;
}> {
  return unwrapPost(postData<Envelope<Record<string, unknown>>>(la('sessions', sessionId, 'audio', 'start'), {}));
}

export async function liveAssistStopAudio(sessionId: string): Promise<Record<string, unknown>> {
  return unwrapPost(postData<Envelope<Record<string, unknown>>>(la('sessions', sessionId, 'audio', 'stop'), {}));
}

export async function liveAssistIngestText(
  sessionId: string,
  body: { text: string; speaker: string; is_final: boolean; start_ms?: number; end_ms?: number }
): Promise<{
  session_id: string;
  accepted: boolean;
  turn?: number;
  result?: Record<string, unknown>;
  session?: LiveAssistSessionPayload;
  opening_turn?: { turn?: number; result?: Record<string, unknown> };
}> {
  return unwrapPost(
    postData<
      Envelope<{
        session_id: string;
        accepted: boolean;
        turn?: number;
        result?: Record<string, unknown>;
        session?: LiveAssistSessionPayload;
        opening_turn?: { turn?: number; result?: Record<string, unknown> };
      }>
    >(la('sessions', sessionId, 'audio', 'ingest-text'), body)
  );
}

export async function liveAssistIngestTranscriptFile(
  sessionId: string,
  filePath: string
): Promise<{
  session?: LiveAssistSessionPayload;
  interaction_id?: number;
  transcript_id?: number;
  ta_id?: number | null;
  tae_id?: number | null;
  ta_status?: string;
  ta_async?: boolean;
  replay_path?: string;
  reused?: boolean;
  resolved_path?: string;
  turns_applied?: number;
}> {
  return unwrapPost(
    postData<
      Envelope<{
        session?: LiveAssistSessionPayload;
        interaction_id?: number;
        transcript_id?: number;
        ta_id?: number | null;
        tae_id?: number | null;
        ta_status?: string;
        ta_async?: boolean;
        replay_path?: string;
        reused?: boolean;
        resolved_path?: string;
        turns_applied?: number;
      }>
    >(la('sessions', sessionId, 'transcript', 'ingest-file'), {
      file_path: filePath,
    })
  );
}

export async function liveAssistTranscribeFile(
  sessionId: string,
  filePath: string
): Promise<{
  session?: LiveAssistSessionPayload;
  interaction_id?: number;
  transcript_id?: number;
  ta_id?: number | null;
  tae_id?: number | null;
  ta_status?: string;
  ta_async?: boolean;
  replay_path?: string;
  reused?: boolean;
  resolved_path?: string;
  turns_applied?: number;
}> {
  return unwrapPost(
    postData<
      Envelope<{
        session?: LiveAssistSessionPayload;
        interaction_id?: number;
        transcript_id?: number;
        ta_id?: number | null;
        tae_id?: number | null;
        ta_status?: string;
        ta_async?: boolean;
        replay_path?: string;
        reused?: boolean;
        resolved_path?: string;
        turns_applied?: number;
      }>
    >(la('sessions', sessionId, 'audio', 'transcribe-file'), {
      file_path: filePath,
    })
  );
}

export async function liveAssistPrepareReplay(
  interactionId: number
): Promise<{
  interaction_id: number;
  transcript_id?: number;
  ta_id?: number | null;
  tae_id?: number | null;
  ta_status?: string;
  ta_async?: boolean;
  replay_path?: string;
  reused?: boolean;
  turns_applied?: number;
}> {
  return unwrapPost(
    postData<
      Envelope<{
        interaction_id: number;
        transcript_id?: number;
        ta_id?: number | null;
        tae_id?: number | null;
        ta_status?: string;
        ta_async?: boolean;
        replay_path?: string;
        reused?: boolean;
        turns_applied?: number;
      }>
    >(la('replay', interactionId, 'prepare'), {})
  );
}
